import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import SoftBox from "../../components/SoftBox";
import SoftTypography from "../../components/SoftTypography";
import { MdLocalPhone, MdPerson, MdHome, MdEmail } from "react-icons/md";
import { LuBuilding } from "react-icons/lu";
import SoftButton from "../../components/SoftButton";
import { IoClose } from "react-icons/io5";
import Divider from '@mui/material/Divider';
import PropTypes from "prop-types";
import {getDepartementsList, updateProfile} from "../../axios-client";
import {ALMANDUMA_CUSTOMER, LIBRARIAN_ROLE, LIBRARY_ADMIN_ROLE} from "../../utlis/constant";
import i18n from "config/i18n";

const EditLibrarianDialog = ({ open, onClose, onLibrarianAdded, librarianData, isSuperAdmin, setSnackbar, t }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        department: '',
        address: "",
    });
    const [errors, setErrors] = useState({});
    const [departments, setDepartments] = useState([]);
    const [isFocused, setIsFocused] = useState(false);
    const customerName = window?.appConfig?.REACT_APP_CUSTOMER_NAME || process.env.REACT_APP_CUSTOMER_NAME

    useEffect(() => {
        if (open && librarianData) {
            setFormData({
                name: librarianData.name || '',
                email: librarianData.email || '',
                phone: librarianData.phone || '',
                department: !isSuperAdmin ? (librarianData.departments ? librarianData.departments.map(department => department.id) : '') : '',
                address: librarianData.address || '',
            });
        }
    }, [open, librarianData, isSuperAdmin]);

    useEffect(() => {
        async function loadDepartments() {
            if (!isSuperAdmin) {
                try {
                    const response = await getDepartementsList()
                    setDepartments(response.data.departments || []);
                } catch (error) {
                    console.error("Failed to load departments", error);
                    setDepartments([]);
                }
            }
        }
        loadDepartments();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const validateForm = () => {
        let formErrors = {};
        if (!formData.name) formErrors.name = t("dialogs.UpdateLibrarianInformation.messages.NameRequired");
        if (!formData.email) formErrors.email = t("dialogs.UpdateLibrarianInformation.messages.EmailRequired");
        if (!isSuperAdmin && !formData.department) formErrors.department = t("dialogs.UpdateLibrarianInformation.messages.DepartmentRequired");
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (formData.email && !emailPattern.test(formData.email)) {
            formErrors.email = t("dialogs.UpdateLibrarianInformation.messages.InvalidEmail");
        }

        return formErrors;
    };

    const handleSubmit = () => {
        const formErrors = validateForm();
        if (Object.keys(formErrors).length === 0) {
            const formDataToSend = new FormData();
            formDataToSend.append('name', formData.name);
            formDataToSend.append('email', formData.email);
            formDataToSend.append('phone', formData.phone);
            formDataToSend.append('address', formData.address);
            if (!isSuperAdmin) {
                formDataToSend.append('departments', formData.department);
            }
            const role = isSuperAdmin ? LIBRARY_ADMIN_ROLE : LIBRARIAN_ROLE;
            updateProfile(librarianData.id, formDataToSend, role).then(res => {
                onLibrarianAdded();
                setSnackbar({ open: true, message: customerName === ALMANDUMA_CUSTOMER ? t('dialogs.UpdateLibrarianInformation.messages.UserUpdated') : t("dialogs.UpdateLibrarianInformation.messages.LibrarianUpdated"), severity: 'success' });
                onClose();
            }).catch(error => {
                setSnackbar({ open: true, message: customerName === ALMANDUMA_CUSTOMER ? t('dialogs.UpdateLibrarianInformation.messages.UserUpdateFailed') : t("dialogs.UpdateLibrarianInformation.messages.LibrarianUpdateFailed"), severity: 'error' });
            })
        } else {
            setErrors(formErrors);
            setSnackbar({ open: true, message: t("dialogs.UpdateLibrarianInformation.messages.FormIncomplete"), severity: 'error' });
        }
    };

    const handleCancel = () => {
        onClose();
    };

    const handleSnackbarClose = () => {
        setSnackbar({ open: false, message: '', severity: '' });
    };
    const handleDepartmentChange = (e) => {
        setFormData({ ...formData, department: e.target.value });
    };

    const inputContainerStyle = {
        display: 'flex',
        alignItems: 'center',
        border: `1px solid ${isFocused ? '#cdcecf' : '#dcdfe3'}`,
        borderRadius: '8px',
        padding: '5px 10px',
        marginBottom: '10px',
        transition: 'border-color 0.3s ease',
    };

    const inputStyle = {
        width: '100%',
        padding: '10px 0px',
        border: 'none',
        fontSize: '14px',
        boxSizing: 'border-box',
        outline: 'none',
        fontFamily: 'Arial, Roboto',
    };

    return (
        <Dialog open={open} maxWidth="sm" fullWidth>
            <DialogTitle
                id="form-dialog-title"
                sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
            >
                {isSuperAdmin ? "Update Librarian Admin Information" :  customerName === ALMANDUMA_CUSTOMER ? t('dialogs.UpdateLibrarianInformation.userTitle') : t('dialogs.UpdateLibrarianInformation.title')}
                <IoClose
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        size: 10
                    }}
                />
            </DialogTitle>
            <Divider/>
            <DialogContent>
                <label htmlFor="name" className="label-style">{t('dialogs.UpdateLibrarianInformation.Name')}</label>
                <div style={inputContainerStyle}>
                    <MdPerson style={{...(i18n.language === "ar" ? {marginLeft: "8px"} : {marginRight: "8px"}), color: "#888"}}/>
                    <input type="text" id="name" name="name" style={inputStyle}
                           value={formData.name}
                           onChange={handleChange}
                           onFocus={() => setIsFocused(true)}
                           onBlur={() => setIsFocused(false)}/>
                </div>
                {errors.name && <p style={{color: 'red', fontSize: "12px"}}>{errors.name}</p>}

                <label htmlFor="email" className="label-style">{t('dialogs.UpdateLibrarianInformation.Email')}</label>
                <div style={inputContainerStyle}>
                    <MdEmail style={{...(i18n.language === "ar" ? {marginLeft: "8px"} : {marginRight: "8px"}), color: "#888"}}/>
                    <input type="text" id="email" name="email" style={inputStyle}
                           value={formData.email}
                           onChange={handleChange}
                           onFocus={() => setIsFocused(true)}
                           onBlur={() => setIsFocused(false)}/>
                </div>
                {errors.email && <p style={{color: 'red', fontSize: "12px"}}>{errors.email}</p>}

                <label htmlFor="phone" className="label-style">{t('dialogs.UpdateLibrarianInformation.Phone')}</label>
                <div style={inputContainerStyle}>
                    <MdLocalPhone style={{...(i18n.language === "ar" ? {marginLeft: "8px"} : {marginRight: "8px"}), color: "#888"}}/>
                    <input type="text" id="phone" name="phone" style={inputStyle}
                           value={formData.phone}
                           onChange={handleChange}
                           onFocus={() => setIsFocused(true)}
                           onBlur={() => setIsFocused(false)}/>
                </div>
                {errors.phone && <p style={{color: 'red', fontSize: "12px"}}>{errors.phone}</p>}

                {!isSuperAdmin && (
                    <>
                        <label htmlFor="department" className="label-style">{t('dialogs.UpdateLibrarianInformation.Department')}</label>

                        <SoftBox mb={2} style={{position: 'relative'}}>
                            <LuBuilding className="icon-style" style={{
                                position: 'absolute',
                                ...(i18n.language === "ar" ? {right: "10px"} : {left: "10px"}),
                                top: '50%',
                                transform: 'translateY(-50%)',
                                pointerEvents: 'none',
                                fontSize: '20px',
                                color: '#666'
                            }}/>

                            <select
                                id="department"
                                name="department"
                                value={formData.department}
                                onChange={handleDepartmentChange}
                                style={{
                                    width: "100%",
                                    ...(i18n.language === "ar" ? {padding: "10px 40px 10px 10px"} : {padding: "10px 10px 10px 40px"}),
                                    borderRadius: "4px",
                                    border: "1px solid #ccc",
                                    fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                                    background: "transparent",
                                }}
                            >
                                <option disabled={true} value="">
                                    Select a department
                                </option>
                                {departments?.map((department) => (
                                    <option key={department.id} value={department.id}>
                                        {department.name}
                                    </option>
                                ))}
                            </select>
                            {errors.department && (
                                <SoftTypography color="error" fontSize="small">
                                    {errors.department}
                                </SoftTypography>
                            )}
                        </SoftBox>
                    </>
                )}
                <label htmlFor="address" className="label-style">{t('dialogs.UpdateLibrarianInformation.Address')}</label>
                <div style={inputContainerStyle}>
                    <MdHome style={{...(i18n.language === "ar" ? {marginLeft: "8px"} : {marginRight: "8px"}), color: "#888"}}/>
                    <input type="text" id="address" name="address" style={inputStyle}
                           value={formData.address}
                           onChange={handleChange}
                           onFocus={() => setIsFocused(true)}
                           onBlur={() => setIsFocused(false)}/>
                </div>
                {errors.address && <p style={{color: 'red', fontSize: "12px"}}>{errors.address}</p>}

            </DialogContent>

            <DialogActions>
                <SoftButton variant="outlined" color="dark" onClick={handleCancel}>
                    {t('dialogs.UpdateLibrarianInformation.ActionButtons.Cancel')}
                </SoftButton>
                <SoftButton variant="gradient" color="primary" onClick={handleSubmit} sx={{ml: 1 , mr:  (i18n.language === 'ar' ? 1 : 0)}}>
                    {t('dialogs.UpdateLibrarianInformation.ActionButtons.Save')}
                </SoftButton>
            </DialogActions>
        </Dialog>
    );
};

EditLibrarianDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onLibrarianAdded: PropTypes.func,
    librarianData: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        email: PropTypes.string,
        phone: PropTypes.string,
        departments: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
        })),
        address: PropTypes.string,
    }),
    isSuperAdmin: PropTypes.bool.isRequired,
    setSnackbar: PropTypes.func,
    t: PropTypes.object
};

export default EditLibrarianDialog;
